import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import type { Usuario } from "../../firebase/firestore/usuarios";
import type { PaqueteForm } from "./PackageItem";

interface Props {
  selectedUser: Usuario;
  paquetes: PaqueteForm[];
  handleBack: () => void;
  handleSave: () => void;
  isLoading: boolean;
}

export default function StepResumen({
  selectedUser,
  paquetes,
  handleBack,
  handleSave,
  isLoading,
}: Props) {
  return (
    <Box
      sx={{
        minHeight: 300,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ px: 2 }}>
        <Typography variant="h6" gutterBottom>
          Resumen del paquete
        </Typography>

        {/* Usuario */}
        <Typography variant="subtitle1" gutterBottom>
          Usuario
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, width: "auto !important" }}>
          {selectedUser ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography>
                  <strong>Nombre:</strong> {selectedUser.name}{" "}
                  {selectedUser.lastName}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography>
                  <strong>Cliente:</strong> {selectedUser.lockerCode}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography>
                  <strong>Email:</strong> {selectedUser.email}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography>
                  <strong>Teléfono:</strong> {selectedUser.phone}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography color="text.secondary">
              No se seleccionó un usuario.
            </Typography>
          )}
        </Paper>

        {/* Paquetes */}
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ marginBottom: 1, marginTop: 1 }}
        >
          Paquetes
        </Typography>

        {paquetes.length === 0 ? (
          <Typography color="text.secondary">
            No hay paquetes cargados.
          </Typography>
        ) : (
          paquetes.map((paquete, index) => (
            <Paper
              key={paquete.id || paquete.idRastreo || `paquete-${index}`}
              variant="outlined"
              sx={{ p: 2, width: "auto !important", marginBottom: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Paquete #{index + 1}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Contenido:</strong> {paquete.contenido || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Rastreo:</strong> {paquete.idRastreo || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Ubicación física:</strong>{" "}
                    {paquete.ubicacionFisica || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Peso:</strong> {paquete.peso?.monto ?? "-"}{" "}
                    {paquete.peso?.unidad ?? ""}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Vía:</strong> {paquete.via || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Tarifa:</strong> {paquete.tarifa?.moneda ?? ""}{" "}
                    {paquete.tarifa?.monto ?? "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography>
                    <strong>Cobro Adicional:</strong> {paquete.tarifa?.moneda ?? ""} {paquete.cobroAdicional ?? 0}
                  </Typography>
                </Grid>
                {(() => {
                  const baseTotal = paquete.tarifa && paquete.peso?.monto 
                    ? paquete.tarifa.monto * paquete.peso.monto 
                    : 0;
                  const cobroAdicional = paquete.cobroAdicional ?? 0;
                  const descuento = paquete.descuento ?? 0;
                  const subtotal = baseTotal + cobroAdicional;
                  const finalTotal = Math.round((subtotal - (descuento > 0 ? descuento : 0)) * 100) / 100;
                  const costo = paquete.costo ?? 0;
                  const gananciaCalculada = paquete.ganancia ?? (finalTotal > 0 && costo > 0 ? Math.round((finalTotal - costo) * 100) / 100 : undefined);
                  const moneda = paquete.tarifa?.moneda ?? "";
                  
                  return (
                    <>
                      {paquete.descuento !== undefined && paquete.descuento !== null && paquete.descuento > 0 && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography>
                            <strong>Descuento:</strong> {moneda} {paquete.descuento}
                          </Typography>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography>
                          <strong>Total:</strong> {
                            paquete.tarifa && paquete.peso?.monto 
                              ? `${moneda} ${finalTotal}`
                              : "-"
                          }
                        </Typography>
                      </Grid>
                      {paquete.costo !== undefined && paquete.costo !== null && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography>
                            <strong>Costo:</strong> {moneda} {paquete.costo}
                          </Typography>
                        </Grid>
                      )}
                      {(paquete.ganancia !== undefined || gananciaCalculada !== undefined) && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography>
                            <strong>Ganancia:</strong> {moneda} {paquete.ganancia ?? gananciaCalculada}
                          </Typography>
                        </Grid>
                      )}
                      {paquete.puntosAcumulados !== undefined && paquete.puntosAcumulados !== null && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography>
                            <strong>Puntos acumulados:</strong> {paquete.puntosAcumulados}
                          </Typography>
                        </Grid>
                      )}
                      {paquete.puntosRedimidos !== undefined && paquete.puntosRedimidos !== null && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography>
                            <strong>Puntos redimidos:</strong> {paquete.puntosRedimidos}
                          </Typography>
                        </Grid>
                      )}
                      {paquete.detallesEntrega && (
                        <Grid size={{ xs: 12 }}>
                          <Typography>
                            <strong>Detalles de entrega:</strong> {paquete.detallesEntrega}
                          </Typography>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography>
                          <strong>Observaciones:</strong> {paquete.observaciones ?? "-"}
                        </Typography>
                      </Grid>
                    </>
                  );
                })()}
                {paquete.id && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography>
                      <strong>Estado:</strong> {String(paquete.estado).replaceAll("_", " ") ?? ""}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
        <Button onClick={handleBack} sx={{ mr: 1 }}>
          Anterior
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </Box>
  );
}
